<template>
  <v-row>
    <v-col cols="12">
      <v-card class="card-container" elevation="0">
        <v-data-table
          :headers="headers"
          :items="pagedItems"
          :item-key="'_id'"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :height="tableHeight"
          fixed-header
          hide-default-footer
        >
          <template v-slot:[`item.sellerName`]="{ item }">
            {{ formatSellerName(item) }}
          </template>

          <template v-slot:[`item.shopName`]="{ item }">
            {{ formatShopName(item) }}
          </template>

          <template v-slot:[`item.email`]="{ item }">
            {{ formatEmail(item) }}
          </template>

          <template v-slot:[`item.phone`]="{ item }">
            {{ formatPhone(item) }}
          </template>

          <template v-slot:[`item.accountStatus`]="{ item }">
            <v-chip
              small
              :color="getAccountStatusDisplay(item).color"
              :text-color="getAccountStatusDisplay(item).text"
              class="status-chip"
            >
              <v-icon v-if="getAccountStatusDisplay(item).icon" left small>
                {{ getAccountStatusDisplay(item).icon }}
              </v-icon>
              {{ getAccountStatusDisplay(item).label }}
            </v-chip>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center flex-wrap" style="gap:4px">
              <v-btn
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('view', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#3B82F6">mdi-eye-outline</v-icon>
                </div>
                <span>รายละเอียด</span>
              </v-btn>

              <!-- inactive = รอ approve -->
              <template v-if="canSuspendSeller && normalizeStatus(item.status) === 'inactive'">
                <v-btn
                  color="#1F2A39"
                  text
                  class="d-flex align-center action-btn"
                  @click="$emit('approve', item)"
                >
                  <div class="detail-btn mr-2">
                    <v-icon size="20" color="#10B981">mdi-check-circle-outline</v-icon>
                  </div>
                  <span>อนุมัติ</span>
                </v-btn>
                <v-btn
                  color="#1F2A39"
                  text
                  class="d-flex align-center action-btn"
                  @click="$emit('reject', item)"
                >
                  <div class="detail-btn mr-2">
                    <v-icon size="20" color="#EF4444">mdi-close-circle-outline</v-icon>
                  </div>
                  <span>ปฏิเสธ</span>
                </v-btn>
              </template>

              <!-- active = ระงับได้ -->
              <v-btn
                v-else-if="canSuspendSeller && normalizeStatus(item.status) === 'active'"
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('toggle-suspend', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#EF4444">mdi-block-helper</v-icon>
                </div>
                <span>ระงับ</span>
              </v-btn>

              <!-- suspended = ยกเลิกระงับได้ -->
              <v-btn
                v-else-if="canSuspendSeller && normalizeStatus(item.status) === 'suspended'"
                color="#1F2A39"
                text
                class="d-flex align-center action-btn"
                @click="$emit('toggle-suspend', item)"
              >
                <div class="detail-btn mr-2">
                  <v-icon size="20" color="#10B981">mdi-lock-open-outline</v-icon>
                </div>
                <span>ยกเลิกระงับ</span>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <v-row class="mt-4" v-if="sellers.length > 10">
        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-start"
        >
          <span class="count-text mr-2">แสดง</span>

          <v-menu v-model="menu" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <div
                class="custom-select"
                v-bind="attrs"
                v-on="on"
                :class="{ 'menu-open': menu }"
              >
                <span class="items-per-page-text">
                  {{ currentItemsPerPageText }}
                </span>
                <img src="@/assets/caret_down.png" class="caret-icon ml-1" />
              </div>
            </template>

            <v-list>
              <v-list-item
                v-for="option in itemsPerPageOptions"
                :key="option.value"
                @click="selectOption(option)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <span class="count-text ml-2">
            จากทั้งหมด {{ sellers.length }} รายการ
          </span>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="d-flex align-center justify-center justify-md-end pa-0"
        >
          <v-pagination
            v-model="page"
            :length="pageCount"
            :total-visible="7"
            prev-icon="ก่อนหน้า"
            next-icon="ถัดไป"
            class="mx-2"
            color="#5C96F8"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "Table",

  props: {
    sellers: {
      type: Array,
      required: true,
    },
    // role ของคนที่ล็อกอินปัจจุบัน (admin / superadmin)
    userRole: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      page: 1,
      menu: false,
      itemsPerPage: 10,
      itemsPerPageOptions: [
        { text: "10", value: 10 },
        { text: "100", value: 100 },
        { text: "ทั้งหมด", value: -1 },
      ],
    };
  },

  computed: {
    pageCount() {
      if (this.itemsPerPage === -1) return 1;
      return Math.ceil(this.sellers.length / this.itemsPerPage);
    },

    pagedItems() {
      if (this.itemsPerPage === -1) return this.sellers;
      const start = (this.page - 1) * this.itemsPerPage;
      return this.sellers.slice(start, start + this.itemsPerPage);
    },

    tableHeight() {
      return this.pagedItems.length < 7 ? "auto" : "380px";
    },

    currentItemsPerPageText() {
      const opt = this.itemsPerPageOptions.find(
        (x) => x.value === this.itemsPerPage
      );
      return opt ? opt.text : this.itemsPerPage;
    },

    normalizedUserRole() {
      return (this.userRole || "").toString().trim().toLowerCase();
    },

    canSuspendSeller() {
      return ["admin", "superadmin"].includes(this.normalizedUserRole);
    },

    headers() {
      return [
        {
          text: "ชื่อร้าน",
          value: "shopName",
          align: "left",
          class: "header-shop",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "ชื่อผู้ขาย",
          value: "sellerName",
          align: "left",
          class: "header-seller",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "อีเมล",
          value: "email",
          align: "left",
          class: "header-email",
          cellClass: "cell-normal",
          sortable: false,
          width: "260px",
        },
        {
          text: "เบอร์โทรศัพท์",
          value: "phone",
          align: "left",
          class: "header-phone",
          cellClass: "cell-normal",
          sortable: false,
          width: "180px",
        },
        {
          text: "สถานะบัญชีผู้ขาย",
          value: "accountStatus",
          align: "left",
          class: "header-account-status",
          cellClass: "cell-normal",
          sortable: false,
          width: "200px",
        },
        {
          text: "วันที่ลงทะเบียน",
          value: "createdAt",
          align: "left",
          class: "header-created",
          cellClass: "cell-normal",
          sortable: false,
          width: "220px",
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "left",
          class: "header-actions",
          cellClass: "cell-actions",
          sortable: false,
          width: "220px",
        },
      ];
    },
  },

  watch: {
    page() {
      this.scrollToTopOfTable();
    },
    sellers() {
      this.page = 1;
      this.scrollToTopOfTable();
    },
  },

  methods: {
    formatSellerName(item) {
      const user = item.user || {};
      const personal = item.personalInfo || {};

      const prefix = (personal.prefix || user.prefix || "").trim();
      const first = (personal.firstName || user.firstName || "").trim();
      const last = (personal.lastName || user.lastName || "").trim();

      const parts = [prefix, first, last].filter(Boolean);

      if (parts.length) {
        return parts.join(" ");
      }

      return user.username || item.shopName || item.shop_name || "-";
    },
    formatShopName(item) {
      const name =
        item.shopName ||
        item.shop_name ||
        item.storeName ||
        item.store_name ||
        item.brandName ||
        item.brand_name ||
        "";
      return (name || "").trim() || "-";
    },

    formatEmail(item) {
      const user = item.user || {};
      return (
        user.email ||
        item.email ||
        item.contactEmail ||
        item.contact_email ||
        "-"
      );
    },

    formatPhone(item) {
      return (
        item.phone ||
        item.phoneNumber ||
        item.phone_number ||
        item.contactPhone ||
        item.contact_phone ||
        "-"
      );
    },

    formatDate(d) {
      if (!d) return "-";

      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";

      const monthsFullThai = [
        "มกราคม",
        "กุมภาพันธ์",
        "มีนาคม",
        "เมษายน",
        "พฤษภาคม",
        "มิถุนายน",
        "กรกฎาคม",
        "สิงหาคม",
        "กันยายน",
        "ตุลาคม",
        "พฤศจิกายน",
        "ธันวาคม",
      ];

      const day = date.getDate();
      const month = monthsFullThai[date.getMonth()];
      const year = date.getFullYear() + 543;

      return `${day} ${month} ${year}`;
    },

    normalizeStatus(status) {
      const v = (status || "").toString().trim().toLowerCase();
      if (v === "active") return "active";
      if (v === "inactive") return "inactive";
      if (["suspended", "ban", "banned"].includes(v)) return "suspended";
      if (v === "rejected") return "rejected";
      return "unknown";
    },

    getAccountStatusDisplay(item) {
      const s = this.normalizeStatus(item.status);

      if (s === "active") {
        return {
          label: "ใช้งานปกติ",
          color: "#E1FAE8",
          text: "#0FAE63",
          icon: "mdi-check-circle-outline",
        };
      }

      if (s === "inactive") {
        return {
          label: "รออนุมัติ",
          color: "#FEF3C7",
          text: "#D97706",
          icon: "mdi-clock-outline",
        };
      }

      if (s === "suspended") {
        return {
          label: "ถูกระงับโดยผู้ดูแลระบบ",
          color: "#FDECEC",
          text: "#E11D48",
          icon: "mdi-block-helper",
        };
      }

      if (s === "rejected") {
        return {
          label: "ถูกปฏิเสธ",
          color: "#FDECEC",
          text: "#E11D48",
          icon: "mdi-close-circle-outline",
        };
      }

      return {
        label: "ไม่ทราบสถานะ",
        color: "#E5E7EB",
        text: "#4B5563",
        icon: "mdi-help-circle-outline",
      };
    },

    getActiveProductCount(seller) {
      const count =
        seller.activeProductsCount ??
        seller.active_products_count ??
        seller.activeProducts ??
        seller.active_products ??
        seller.productCount ??
        seller.products_count ??
        0;

      const num = Number(count);
      return Number.isNaN(num) ? 0 : num;
    },

    selectOption(option) {
      this.itemsPerPage = option.value;
      this.page = 1;
      this.menu = false;
    },

    scrollToTopOfTable() {
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector(".v-data-table__wrapper");
        if (wrapper) {
          wrapper.scrollTo({ top: 0 });
        } else {
          window.scrollTo({ top: 0 });
        }
      });
    },
  },
};
</script>

<style scoped>
.card-container {
  overflow: hidden !important;
  border-radius: 12px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.cell-normal),
::v-deep(.cell-actions) {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #1f2a39 !important;
}
::v-deep(.header-seller),
::v-deep(.header-shop),
::v-deep(.header-email),
::v-deep(.header-phone),
::v-deep(.header-account-status),
::v-deep(.header-created),
::v-deep(.header-actions) {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #6b717f !important;
  background-color: #f9fafc !important;
  box-shadow: none !important;
}
.status-chip {
  border-radius: 8px !important;
}
.count-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.items-per-page-text {
  font-size: 14px;
  font-weight: 400;
  color: #394253;
}
.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
}
.caret-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}
.custom-select.menu-open .caret-icon {
  transform: rotate(180deg);
}
::v-deep(.cell-actions) {
  position: sticky;
  right: 0;
  background: #fff;
  z-index: 2;
}
::v-deep(.header-actions) {
  position: sticky;
  right: 0;
  background: #f9fafc;
  box-shadow: -2px 0 3px -1px #1018280f !important;
  z-index: 3;
}
::v-deep .v-pagination__item,
::v-deep .v-pagination__navigation {
  font-size: 14px !important;
  font-weight: 400 !important;
  border-radius: 8px !important;
  width: 34px !important;
  height: 34px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
}
::v-deep(.v-pagination .v-pagination__navigation .v-icon) {
  font-size: 12px !important;
  font-weight: 400 !important;
  color: #6b717f !important;
  font-family: "Noto Sans Thai", sans-serif !important;
  font-style: normal !important;
}
::v-deep(.v-pagination .v-pagination__navigation) {
  width: 65px !important;
  height: 34px !important;
}
.action-btn {
  min-width: 0 !important;
  padding: 0 4px !important;
}
.detail-btn {
  border-radius: 4px !important;
  padding: 2px !important;
  box-shadow: 0px 0px 2px 0px #1018280f, 0px 0px 3px 0px #1018281a !important;
  width: 24px !important;
  height: 24px !important;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>